/**
 * Minimal, auto-hiding toolbar. Module controls are rendered from each module's
 * declarative ParamSpec, so the host owns the look and third-party modules can't
 * fight the aesthetic.
 */
import type { Engine } from '../core/engine';
import type { SceneRenderer } from '../render/renderer';
import type { AffectModule, CreateModule, ModuleManifest, ParamSpec } from '../core/module-api';
import type { RGBA } from '../core/scene';
import { colorToHex, cssColor, parseColor } from '../core/scene';
import { exportPngFile, exportSvgFile } from '../export/exporters';

function iconButton(svg: string, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'alc-btn';
  b.type = 'button';
  b.innerHTML = svg;
  b.title = label;
  b.setAttribute('aria-label', label);
  return b;
}

/** Icon + text-label button for the "More" drawer, where the rarer tools live
 * — the label makes them legible instead of a wall of cryptic glyphs. */
function labeledButton(svg: string, label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'alc-btn alc-more-btn';
  b.type = 'button';
  b.innerHTML = `${svg}<span>${label}</span>`;
  b.title = label;
  b.setAttribute('aria-label', label);
  return b;
}

const ICONS = {
  undo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-3"/></svg>',
  clear:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/></svg>',
  png: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>',
  svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>',
  full: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/><path d="M8 21H5a2 2 0 0 1-2-2v-3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>',
  download:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg>',
  more: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="12" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="19" cy="12" r="1.4"/></svg>',
};

export function buildToolbar(
  engine: Engine,
  renderer: SceneRenderer,
  creates: CreateModule[],
  affects: AffectModule[],
  onOpenLibrary?: () => void,
  onTrace?: () => void,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'alc-ui';

  const bar = document.createElement('div');
  bar.className = 'alc-toolbar';
  root.appendChild(bar);

  const brand = document.createElement('button');
  brand.type = 'button';
  brand.className = 'alc-brand';
  brand.title = 'About Alchemy';
  brand.setAttribute('aria-label', 'About Alchemy');
  brand.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><circle cx="12" cy="13" r="6"/><path d="M12 3 19 16H5z"/></svg>';
  bar.appendChild(brand);

  const menu = document.createElement('div');
  menu.className = 'alc-menu';
  menu.hidden = true;
  menu.innerHTML =
    '<div class="alc-menu-title">Alchemy</div>' +
    '<div class="alc-menu-sub">An open drawing project, reborn for the web. Pick one draw tool, then stack affects to warp it. There is no wrong mark.</div>' +
    '<div class="alc-menu-keys"><span><kbd>⌘Z</kbd> Undo</span><span><kbd>⌘⇧P</kbd> PNG</span><span><kbd>⌘⇧S</kbd> SVG</span></div>' +
    '<div class="alc-menu-actions">' +
    '<button type="button" class="alc-menu-btn" data-act="new">New sketch</button>' +
    '<button type="button" class="alc-menu-btn ghost" data-act="sets">Shape sets</button>' +
    '<button type="button" class="alc-menu-btn ghost" data-act="trace">Trace image</button>' +
    '<a class="alc-menu-link" href="https://al.chemy.org" target="_blank" rel="noopener noreferrer">The original ↗</a>' +
    '</div>';
  root.appendChild(menu);

  // Download-format popover (one Download button → choose PNG or SVG). Lives
  // right after the brand menu so it drops down under the toolbar the same way.
  const exportMenu = document.createElement('div');
  exportMenu.className = 'alc-menu alc-export-menu';
  exportMenu.hidden = true;
  exportMenu.innerHTML =
    '<div class="alc-menu-title">Download</div>' +
    '<div class="alc-menu-sub">Choose a format. SVG keeps everything as editable vectors; PNG is a flat high-resolution image.</div>' +
    '<div class="alc-menu-actions">' +
    '<button type="button" class="alc-menu-btn" data-fmt="svg">SVG — vector</button>' +
    '<button type="button" class="alc-menu-btn ghost" data-fmt="png">PNG — image</button>' +
    '</div>';
  root.appendChild(exportMenu);

  // "More" drawer — the rarer/experimental tools live here so the everyday
  // toolbar stays uncluttered. Their buttons are still registered in the same
  // create/affect maps below, so active-state highlighting keeps working.
  const NICHE_CREATE = new Set(['mic', 'pitch', 'xshapes', 'inverse', 'median', 'detach']);
  const NICHE_AFFECT = new Set(['blindness', 'shapeswap', 'micexpand']);
  const moreMenu = document.createElement('div');
  moreMenu.className = 'alc-menu alc-more-menu';
  moreMenu.hidden = true;
  const moreCreateRow = document.createElement('div');
  moreCreateRow.className = 'alc-more-row';
  const moreAffectRow = document.createElement('div');
  moreAffectRow.className = 'alc-more-row';
  const moreLabel = (t: string): HTMLElement => {
    const el = document.createElement('div');
    el.className = 'alc-more-label';
    el.textContent = t;
    return el;
  };
  const moreTitle = document.createElement('div');
  moreTitle.className = 'alc-menu-title';
  moreTitle.textContent = 'More tools';
  moreMenu.append(moreTitle, moreLabel('Draw — pick one'), moreCreateRow, moreLabel('Affect — stack any'), moreAffectRow);
  root.appendChild(moreMenu);

  const closeMenu = (): void => {
    menu.hidden = true;
  };
  const closeExportMenu = (): void => {
    exportMenu.hidden = true;
  };
  const closeMore = (): void => {
    moreMenu.hidden = true;
  };
  brand.addEventListener('click', (e) => {
    e.stopPropagation();
    closeExportMenu();
    closeMore();
    menu.hidden = !menu.hidden;
  });
  menu.querySelector('[data-act="new"]')?.addEventListener('click', () => {
    engine.clear();
    closeMenu();
  });
  menu.querySelector('[data-act="sets"]')?.addEventListener('click', () => {
    onOpenLibrary?.();
    closeMenu();
  });
  menu.querySelector('[data-act="trace"]')?.addEventListener('click', () => {
    onTrace?.();
    closeMenu();
  });
  document.addEventListener('pointerdown', (e) => {
    const t = e.target as Node;
    if (!menu.hidden && !menu.contains(t) && !brand.contains(t)) closeMenu();
  });

  const inkStrip = buildInkStrip(engine);
  bar.appendChild(inkStrip.el);
  sepEl(bar);

  const createGroup = groupEl(bar);
  const createButtons = new Map<CreateModule, HTMLButtonElement>();
  for (const m of creates) {
    const niche = NICHE_CREATE.has(m.manifest.id);
    const b = niche
      ? labeledButton(m.manifest.icon ?? dot(), m.manifest.name)
      : iconButton(m.manifest.icon ?? dot(), m.manifest.name);
    b.addEventListener('click', () => {
      engine.setCreate(m);
      // Selecting Trace with nothing loaded yet: open the picker straight away,
      // so the tool reads as "trace over an image" instead of doing nothing.
      if (m.manifest.id === 'trace' && onTrace && !engine.getTraceImage()) onTrace();
    });
    (niche ? moreCreateRow : createGroup).appendChild(b);
    createButtons.set(m, b);
  }

  sepEl(bar);

  const affectGroup = groupEl(bar);
  const affectButtons = new Map<AffectModule, HTMLButtonElement>();
  for (const m of affects) {
    const niche = NICHE_AFFECT.has(m.manifest.id);
    const b = niche
      ? labeledButton(m.manifest.icon ?? dot(), m.manifest.name)
      : iconButton(m.manifest.icon ?? dot(), m.manifest.name);
    b.addEventListener('click', () => engine.toggleAffect(m));
    (niche ? moreAffectRow : affectGroup).appendChild(b);
    affectButtons.set(m, b);
  }

  const moreBtn = iconButton(ICONS.more, 'More tools');
  moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    closeMenu();
    closeExportMenu();
    moreMenu.hidden = !moreMenu.hidden;
  });
  affectGroup.appendChild(moreBtn);
  document.addEventListener('pointerdown', (e) => {
    const t = e.target as Node;
    if (!moreMenu.hidden && !moreMenu.contains(t) && !moreBtn.contains(t)) closeMore();
  });

  sepEl(bar);

  const actions = groupEl(bar);
  const undoBtn = iconButton(ICONS.undo, 'Undo (⌘Z)');
  undoBtn.addEventListener('click', () => engine.undo());
  const clearBtn = iconButton(ICONS.clear, 'Clear');
  clearBtn.addEventListener('click', () => engine.clear());
  const downloadBtn = iconButton(ICONS.download, 'Download…');
  downloadBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    closeMenu();
    closeMore();
    exportMenu.hidden = !exportMenu.hidden;
  });
  exportMenu.querySelector('[data-fmt="svg"]')?.addEventListener('click', () => {
    exportSvgFile(engine.scene);
    closeExportMenu();
  });
  exportMenu.querySelector('[data-fmt="png"]')?.addEventListener('click', () => {
    exportPngFile(renderer, engine.scene, 2);
    closeExportMenu();
  });
  document.addEventListener('pointerdown', (e) => {
    const t = e.target as Node;
    if (!exportMenu.hidden && !exportMenu.contains(t) && !downloadBtn.contains(t)) closeExportMenu();
  });
  const fullBtn = iconButton(ICONS.full, 'Fullscreen');
  fullBtn.addEventListener('click', () => toggleFullscreen());
  for (const b of [undoBtn, clearBtn, downloadBtn, fullBtn]) actions.appendChild(b);

  const params = document.createElement('div');
  params.className = 'alc-params';
  root.appendChild(params);

  const mic = document.createElement('div');
  mic.className = 'alc-mic';
  mic.hidden = true;
  mic.innerHTML = '<span class="alc-mic-dot"></span> mic live';
  root.appendChild(mic);

  // Standalone status pill, shown only while the Blind affect is on, so the
  // canvas blanking mid-stroke reads as an enabled mode, not a glitch. Lives on
  // <body> (not in the auto-hiding toolbar) so it stays visible during a stroke.
  const blindHint = document.createElement('div');
  blindHint.className = 'alc-blind-hint';
  blindHint.hidden = true;
  blindHint.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3l18 18"/><path d="M10.6 5.1A9 9 0 0 1 21 12a9 9 0 0 1-1.6 2.5"/><path d="M6.6 6.6A9 9 0 0 0 3 12a9 9 0 0 0 12.5 6.5"/></svg>' +
    '<span>Blind — your marks stay hidden until you lift the pen</span>';
  document.body.appendChild(blindHint);

  const update = (): void => {
    for (const [m, b] of createButtons) b.classList.toggle('active', engine.activeCreate === m);
    for (const [m, b] of affectButtons) b.classList.toggle('active', engine.isAffectActive(m));
    // Light up "More" when an active tool is hidden inside it, so a niche tool
    // in use is never invisible.
    const nicheActive =
      NICHE_CREATE.has(engine.activeCreate.manifest.id) ||
      affects.some((a) => NICHE_AFFECT.has(a.manifest.id) && engine.isAffectActive(a));
    moreBtn.classList.toggle('active', nicheActive);
    undoBtn.disabled = !engine.canUndo();
    mic.hidden = !engine.isAudioEnabled();
    blindHint.hidden = !affects.some((a) => a.manifest.id === 'blindness' && engine.isAffectActive(a));
    inkStrip.refresh();
    renderParams();
  };

  const renderParams = (): void => {
    // Don't nuke a focused text field (e.g. the Type tool's text) when an
    // unrelated onChange fires (async restore / library load) — that would drop
    // focus, caret, and any in-flight IME composition.
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.type === 'text' && params.contains(active)) return;
    params.innerHTML = '';
    const mods: ModuleManifest[] = [engine.activeCreate.manifest, ...affects.filter((a) => engine.isAffectActive(a)).map((a) => a.manifest)];
    for (const manifest of mods) {
      const isTrace = manifest.id === 'trace' && !!onTrace;
      const hasParams = !!manifest.params && manifest.params.length > 0;
      if (!hasParams && !isTrace) continue;
      const row = document.createElement('div');
      row.className = 'alc-param-row';
      const name = document.createElement('span');
      name.className = 'alc-param-name';
      name.textContent = manifest.name;
      row.appendChild(name);
      // Trace needs an explicit upload control — otherwise the tool looks dead
      // until you happen to find the logo menu or drag a file in.
      if (isTrace) {
        const up = document.createElement('button');
        up.type = 'button';
        up.className = 'alc-param-btn';
        up.textContent = engine.getTraceImage() ? 'Replace image' : 'Choose image';
        up.addEventListener('click', () => onTrace?.());
        row.appendChild(up);
      }
      if (manifest.params) for (const spec of manifest.params) row.appendChild(renderParam(engine, manifest.id, spec));
      params.appendChild(row);
    }
  };

  engine.onChange = update;
  setupAutoHide(root);
  update();
  return root;
}

function renderParam(engine: Engine, moduleId: string, spec: ParamSpec): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'alc-param';
  const cfg = engine.getConfig(moduleId);

  if (spec.kind === 'slider') {
    wrap.append(text(spec.label));
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(spec.min);
    input.max = String(spec.max);
    input.step = String(spec.step);
    input.value = String(typeof cfg[spec.key] === 'number' ? cfg[spec.key] : spec.default);
    input.addEventListener('input', () => engine.setParam(moduleId, spec.key, Number(input.value)));
    wrap.appendChild(input);
  } else if (spec.kind === 'color') {
    wrap.append(text(spec.label));
    const input = document.createElement('input');
    input.type = 'color';
    input.value = String(typeof cfg[spec.key] === 'string' ? cfg[spec.key] : spec.default);
    input.addEventListener('input', () => engine.setParam(moduleId, spec.key, input.value));
    wrap.appendChild(input);
  } else if (spec.kind === 'select') {
    wrap.append(text(spec.label));
    const select = document.createElement('select');
    for (const opt of spec.options) {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      select.appendChild(o);
    }
    select.value = String(typeof cfg[spec.key] === 'string' ? cfg[spec.key] : spec.default);
    select.addEventListener('change', () => engine.setParam(moduleId, spec.key, select.value));
    wrap.appendChild(select);
  } else if (spec.kind === 'toggle') {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = Boolean(typeof cfg[spec.key] === 'boolean' ? cfg[spec.key] : spec.default);
    input.addEventListener('change', () => engine.setParam(moduleId, spec.key, input.checked));
    wrap.appendChild(input);
    wrap.append(text(spec.label));
  } else if (spec.kind === 'text') {
    wrap.append(text(spec.label));
    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'alc-text-input';
    field.value = String(typeof cfg[spec.key] === 'string' ? cfg[spec.key] : spec.default);
    if (spec.placeholder) field.placeholder = spec.placeholder;
    field.addEventListener('input', () => engine.setParam(moduleId, spec.key, field.value));
    wrap.appendChild(field);
  } else {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'alc-param-btn';
    button.textContent = spec.label;
    wrap.appendChild(button);
  }
  return wrap;
}

function text(s: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.textContent = s;
  return span;
}

function groupEl(parent: HTMLElement): HTMLDivElement {
  const g = document.createElement('div');
  g.className = 'alc-group';
  parent.appendChild(g);
  return g;
}

function sepEl(parent: HTMLElement): void {
  const s = document.createElement('div');
  s.className = 'alc-sep';
  parent.appendChild(s);
}

function dot(): string {
  return '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5"/></svg>';
}

type InkMode = { t: 'edit'; i: number } | { t: 'bg' } | { t: 'add' };

/** The "ink strip": canvas background well + an editable session swatch palette. */
function buildInkStrip(engine: Engine): { el: HTMLElement; refresh: () => void } {
  const strip = document.createElement('div');
  strip.className = 'alc-ink';

  const well = document.createElement('button');
  well.type = 'button';
  well.className = 'alc-bg-well';
  well.title = 'Canvas background';
  well.setAttribute('aria-label', 'Canvas background');

  const sep = document.createElement('div');
  sep.className = 'alc-sep';

  const row = document.createElement('div');
  row.className = 'alc-swatch-row';

  const add = document.createElement('button');
  add.type = 'button';
  add.className = 'alc-swatch-add';
  add.title = 'Add a colour';
  add.setAttribute('aria-label', 'Add a colour');
  add.textContent = '+';

  const input = document.createElement('input');
  input.type = 'color';
  input.className = 'alc-color-input';

  strip.append(well, sep, row, add, input);

  let mode: InkMode | null = null;
  const apply = (): void => {
    if (!mode) return;
    const c = parseColor(input.value);
    if (mode.t === 'add') {
      // Only create the swatch once a colour is actually chosen; further input
      // events from the same picker edit the now-active swatch.
      engine.addSwatch(c);
      mode = { t: 'edit', i: engine.getActiveInkIndex() };
    } else if (mode.t === 'edit') {
      engine.editSwatch(mode.i, c);
    } else {
      engine.setBackground(c);
    }
  };
  input.addEventListener('input', apply);
  input.addEventListener('change', apply);

  const openPicker = (m: InkMode, seed: RGBA): void => {
    mode = m;
    input.value = colorToHex(seed);
    input.click();
  };

  well.addEventListener('click', () => openPicker({ t: 'bg' }, engine.getBackground()));
  add.addEventListener('click', () => openPicker({ t: 'add' }, { r: 90, g: 120, b: 255, a: 1 }));

  const refresh = (): void => {
    well.style.background = cssColor(engine.getBackground());
    const palette = engine.getPalette();
    const active = engine.getActiveInkIndex();
    const removable = palette.length > 1;
    row.innerHTML = '';
    palette.forEach((color, i) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = i === active ? 'alc-swatch active' : 'alc-swatch';
      chip.style.background = cssColor(color);
      chip.title = 'Click to use · double-click to edit';
      chip.setAttribute('aria-label', `Ink ${i + 1}`);
      const edit = (): void => openPicker({ t: 'edit', i }, engine.getPalette()[i] ?? engine.getActiveInk());
      chip.addEventListener('click', () => engine.setActiveInk(i));
      chip.addEventListener('dblclick', (e) => {
        e.preventDefault();
        edit();
      });
      addLongPress(chip, edit);
      if (removable) {
        const x = document.createElement('span');
        x.className = 'alc-swatch-x';
        x.textContent = '×';
        x.title = 'Remove this colour';
        x.addEventListener('click', (e) => {
          e.stopPropagation();
          engine.removeSwatch(i);
        });
        chip.appendChild(x);
      }
      row.appendChild(chip);
    });
  };

  return { el: strip, refresh };
}

/** Long-press (touch/pen) to invoke an action; mouse uses double-click instead. */
function addLongPress(el: HTMLElement, fn: () => void): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    cancel();
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, 450);
  });
  el.addEventListener('pointerup', cancel);
  el.addEventListener('pointerleave', cancel);
  el.addEventListener('pointercancel', cancel);
}

function toggleFullscreen(): void {
  if (document.fullscreenElement) {
    void document.exitFullscreen();
  } else {
    void document.documentElement.requestFullscreen().catch(() => undefined);
  }
}

/** The chrome fades away when idle and returns on pointer movement. */
function setupAutoHide(ui: HTMLElement): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let hovering = false;
  const show = (): void => {
    ui.classList.remove('idle');
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      if (!hovering) ui.classList.add('idle');
    }, 3200);
  };
  ui.addEventListener('pointerenter', () => {
    hovering = true;
    show();
  });
  ui.addEventListener('pointerleave', () => {
    hovering = false;
    show();
  });
  window.addEventListener('pointermove', show, { passive: true });
  window.addEventListener('pointerdown', show, { passive: true });
  show();
}
